// Inspecteur · Partner role-views (FRONTEND-BLUEPRINT §3.1 + §4 "Partner unification").
// The legacy app showed five Inspecteur party registers (Verwerkingsverantwoordelijken, Verwerkers,
// Ontvangers, Joint controllers, Contactpersonen). The data layer unifies them into ONE Partner aggregate
// carrying a `role` (REBUILD-DATA-MODEL §4.1, OWNER Issue 1) — so this screen reads the single partner-role
// register and presents it as five role-filtered tabs. W34/D5: a role now roots on a tenant external_party
// (the directory), so a "Rol toevoegen" action assigns a role to a directory party (create unblocked).
import { useMemo, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { api } from "../../lib/api";
import { useI18n } from "../../lib/i18n";
import { Can } from "../../lib/rbac";
import { PageHeader } from "@/components/page-header";
import { DataTable, type Column } from "@/components/data-table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

interface PartnerRoleRow extends Record<string, unknown> {
  id: string;
  role: string;
  externalPartyId: string | null;
  partyName: string | null;
  partyKind: string | null;
  partyCountry: string | null;
}
interface PartyRow { id: string; name: string; partyKind: string | null }

const TABS = [
  { value: "controllers", tabKey: "ins.partners.tab.controllers", emptyKey: "ins.partners.empty.controllers", roles: ["controller"] },
  { value: "processors", tabKey: "ins.partners.tab.processors", emptyKey: "ins.partners.empty.processors", roles: ["data_processor", "sub_processor"] },
  { value: "recipients", tabKey: "ins.partners.tab.recipients", emptyKey: "ins.partners.empty.recipients", roles: ["data_recipient"] },
  { value: "jointControllers", tabKey: "ins.partners.tab.jointControllers", emptyKey: "ins.partners.empty.jointControllers", roles: ["joint_controller"] },
  { value: "contacts", tabKey: "ins.partners.tab.contacts", emptyKey: "ins.partners.empty.contacts", roles: ["representative_art27", "dpo_art37"] },
] as const;

const ROLE_OPTIONS = ["controller", "data_processor", "sub_processor", "data_recipient", "joint_controller", "representative_art27", "dpo_art37"];

export function PartnersRoute() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { data, isLoading, error } = useQuery({ queryKey: ["partner-roles"], queryFn: () => api.listPartnerRoles() as Promise<PartnerRoleRow[]> });
  const rows = useMemo(() => (data ?? []) as PartnerRoleRow[], [data]);

  const columns: Column<PartnerRoleRow>[] = [
    { key: "partyName", header: t("ins.partners.col.partner"), render: (r) => r.partyName ?? "—" },
    { key: "partyCountry", header: t("ins.parties.field.country"), render: (r) => r.partyCountry ?? "—" },
    { key: "role", header: t("ins.partners.col.role"), render: (r) => <Badge variant="secondary">{roleLabel(t, r.role)}</Badge> },
  ];

  return (
    <Can perm="partner_relationship.read" fallback={<PageHeader title={t("ins.partners.title")} description={t("crud.readonly")} />}>
      <section>
        <PageHeader title={t("ins.partners.title")} description={t("ins.partners.sub")} />
        <Can perm="partner_relationship.write">
          <div className="mb-4"><AddRoleDialog /></div>
        </Can>
        {error && <p className="mb-4 text-sm text-destructive">{t("ins.partners.failed")} {(error as Error).message}</p>}
        <Tabs defaultValue={TABS[0].value}>
          <TabsList variant="line">
            {TABS.map((tab) => <TabsTrigger key={tab.value} value={tab.value}>{t(tab.tabKey)}</TabsTrigger>)}
          </TabsList>
          {TABS.map((tab) => {
            const filtered = rows.filter((r) => (tab.roles as readonly string[]).includes(r.role));
            return (
              <TabsContent key={tab.value} value={tab.value} className="mt-4">
                <Card className="p-4">
                  <DataTable columns={columns} rows={filtered} loading={isLoading} empty={t(tab.emptyKey)} getRowId={(r) => r.id}
                    onRowClick={(r) => { if (r.id) void navigate({ to: "/partner-roles/$id", params: { id: String(r.id) } }); }} />
                </Card>
              </TabsContent>
            );
          })}
        </Tabs>
      </section>
    </Can>
  );
}

// Assign a role to a directory party. Pulls the external-party directory for the picker; if it's empty it
// links to the Externe partijen screen so the user adds a party first.
function AddRoleDialog() {
  const { t } = useI18n();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [partyId, setPartyId] = useState("");
  const [role, setRole] = useState("data_processor");
  const { data: parties } = useQuery({ queryKey: ["external-parties"], queryFn: () => api.listExternalParties() as Promise<PartyRow[]>, enabled: open });
  const create = useMutation({
    mutationFn: () => api.createPartnerRole({ externalPartyId: partyId, role } as Parameters<typeof api.createPartnerRole>[0]),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["partner-roles"] }); setOpen(false); setPartyId(""); },
  });
  const list = (parties ?? []) as PartyRow[];

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm"><Plus aria-hidden />{t("ins.partners.addRole")}</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>{t("ins.partners.addRole")}</DialogTitle></DialogHeader>
        {list.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t("ins.partners.noParties")}</p>
        ) : (
          <div className="flex flex-col gap-4">
            <div className="grid gap-1.5">
              <Label htmlFor="ap-party">{t("ins.partners.col.partner")}</Label>
              <Select value={partyId} onValueChange={setPartyId}>
                <SelectTrigger id="ap-party"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>{list.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="ap-role">{t("ins.partners.col.role")}</Label>
              <Select value={role} onValueChange={setRole}>
                <SelectTrigger id="ap-role"><SelectValue /></SelectTrigger>
                <SelectContent>{ROLE_OPTIONS.map((r) => <SelectItem key={r} value={r}>{roleLabel(t, r)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {create.isError && <p className="text-sm text-destructive">{(create.error as Error).message}</p>}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>{t("common.cancel")}</Button>
          <Button onClick={() => create.mutate()} disabled={!partyId || create.isPending}>{create.isPending ? t("crud.saving") : t("common.save")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function roleLabel(t: (key: string) => string, role: string): string {
  const key = `ins.partners.role.${role}`;
  const label = t(key);
  return label === key ? role : label;
}
